// BadgesSection.jsx - Cleaned version with mock and debug code removed
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { getUserBadgesWithProgress, getBadgeStats, getNextAchievableBadges } from './badgeSystem';
import { useNavigate } from 'react-router-dom';

const BADGE_REDIRECTS = {
  blogger: '/blog/create',
  first_post: '/create-post',
  interpreter: '/forum',
  consistent: '/forum',
  streak_7: '/forum',
  prolific_writer: '/blog/create',
  social_butterfly: '/forum',
  streak_30: '/forum',
  streak_100: '/forum',
  streak_365: '/forum',
};

const ProgressBar = ({ percentage, className = "" }) => (
  <div className={`w-full bg-gray-700 rounded-full h-1 mt-2 ${className}`}>
    <div
      className="bg-gradient-to-r from-blue-500 to-purple-500 h-1 rounded-full transition-all duration-500"
      style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
    />
  </div>
);

const BadgeCard = ({ badge, index, navigate }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const redirectPath = BADGE_REDIRECTS[badge.id];
  const isClickable = !badge.unlocked && redirectPath;

  const handleClick = () => {
    if (isClickable) {
      navigate(redirectPath);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{
        y: badge.unlocked ? -5 : -2,
        scale: badge.unlocked ? 1.03 : 1.01
      }}
      className={`${badge.color} backdrop-blur-sm rounded-lg p-4 flex flex-col items-center text-center transition-all duration-300 shadow-lg relative 
        ${!badge.unlocked ? 'grayscale blur-[0.5px] opacity-80' : ''}
        ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-blue-500/50 hover:grayscale-0 hover:opacity-100' : 'cursor-default'}
      `}
      onClick={handleClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2">
        {badge.icon}
      </div>

      <h3 className="font-medium text-sm">{badge.name}</h3>
      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{badge.description}</p>

      {!badge.unlocked && typeof badge.progressPercentage === 'number' && (
        <div className="w-full mt-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-500">
              {badge.progressPercentage}%
            </span>
            {badge.isAchievable && (
              <span className="text-xs text-green-400 font-medium">Ready!</span>
            )}
          </div>
          <ProgressBar percentage={badge.progressPercentage} />
          <p className="text-xs text-gray-500 mt-1">{badge.progressText}</p>
        </div>
      )}

      {badge.unlocked && (
        <div className="mt-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
            ✓ Earned
          </span>
        </div>
      )}

      {/* Enhanced Tooltip */}
      {showTooltip && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-20 pointer-events-none"
        >
          <div className="bg-gray-900/95 backdrop-blur-sm text-white text-sm rounded-lg px-3 py-2 w-64 border border-gray-600 shadow-2xl">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{badge.name}</p>
                <p className="text-gray-300 mt-1 text-sm leading-relaxed">{badge.description}</p>

                {badge.unlocked ? (
                  <div className="mt-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 font-medium">
                      ✓ Badge Earned
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {badge.progressText && (
                      <p className="text-blue-400 text-sm font-medium">
                        Progress: {badge.progressText}
                      </p>
                    )}

                    {badge.current !== undefined && badge.required !== undefined && (
                      <div className="bg-gray-800/50 rounded-lg p-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-xs">Current Progress</span>
                          <span className="text-white text-xs font-medium">
                            {badge.current}/{badge.required}
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-1">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-1 rounded-full transition-all duration-500"
                            style={{ width: `${badge.progressPercentage}%` }}
                          />
                        </div>
                        {badge.remaining > 0 && (
                          <p className="text-orange-400 text-xs mt-1">
                            {badge.remaining} more needed
                          </p>
                        )}
                      </div>
                    )}

                    {isClickable && (
                       <div className="mt-2 text-center">
                         <span className="text-xs text-blue-300 font-medium animate-pulse">
                           Click to start earning! ➜
                         </span>
                       </div>
                    )}

                    {badge.isAchievable && !isClickable && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                        <p className="text-green-400 text-sm font-medium">Ready to earn!</p>
                      </div>
                    )}
                  </div>
                )}

                {badge.status && (
                  <div className="mt-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.status === 'earned' || badge.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        badge.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                      }`}>
                      {badge.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const BadgeStats = ({ stats }) => (
  <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
    <span className="flex items-center gap-1">
      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
      {stats.earned}/{stats.total} Earned
    </span>
    <span className="flex items-center gap-1">
      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
      {stats.inProgress} In Progress
    </span>
    <span className="ml-auto font-medium text-gray-300">
      {stats.completion}% Complete
    </span>
  </div>
);

const NextGoals = ({ badges, summary }) => {
  let nextBadges = [];

  // Use backend summary for closest badge if available
  if (summary?.next_badge) {
    const nextBadge = badges.find(badge => badge.id === summary.next_badge.id);
    if (nextBadge && !nextBadge.unlocked) {
      nextBadges.push(nextBadge);
    }
  }

  // Fill remaining slots with other high-progress badges
  const otherBadges = getNextAchievableBadges(badges, 3)
    .filter(badge => badge.id !== summary?.next_badge?.id);

  nextBadges = [...nextBadges, ...otherBadges].slice(0, 3);

  if (nextBadges.length === 0) return null;

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg">
      <div className="space-y-3">
        {nextBadges.map((badge, index) => (
          <div key={badge.id} className={`flex items-center  ${index === 0 && summary?.next_badge?.id === badge.id ? 'bg-blue-500/5 rounded-lg p-2' : ''}`}>
            <div className="w-6 h-6 flex items-center justify-center">
              {React.cloneElement(badge.icon, { className: "w-5 h-5" })}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">
                  {badge.name}
                  {index === 0 && summary?.next_badge?.id === badge.id && (
                    <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                      Next
                    </span>
                  )}
                </span>
                <span className="text-xs text-gray-400">{badge.progressText}</span>
              </div>
              <ProgressBar percentage={badge.progressPercentage} className="mt-1" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BadgesSection = ({ userData, showAllBadges, setShowAllBadges }) => {
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ earned: 0, total: 0, inProgress: 0, achievable: 0, completion: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const { user, getAuthenticatedApi } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBadges = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const api = getAuthenticatedApi?.();
        const isOwnProfile = !userData?.id || (user && userData.id === user.user_id);
        const userId = userData?.id || user?.user_id || user?.id;

        const processed = await getUserBadgesWithProgress(userData, userId, api, isOwnProfile);
        setBadges(processed);

        // Store the original progress data for next goals
        if (api && userId) {
          try {
            let response;
            if (isOwnProfile) {
              response = await api.get('/profile/me/badge-progress');
            } else {
              response = await api.get(`/profile/user/${userId}/gamification`);
            }
            setProgressData(response.data);
          } catch (apiError) {
            console.warn('Could not fetch badge progress data:', apiError);
          }
        }


        const badgeStats = getBadgeStats(processed, progressData);
        setStats(badgeStats);

      } catch (error) {
        console.error('Error fetching badges:', error);
        setError('Failed to load badges');
        setBadges([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBadges();
  }, [userData, user, getAuthenticatedApi]);

  const visibleBadges = showAllBadges ? badges : badges.slice(0, 6);

  if (error) {
    return (
      <div className="mt-4">
        <h2 className="text-xl font-semibold mb-4">Badges</h2>
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-blue-400 hover:text-blue-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Badges</h2>
        {badges.length > 6 && (
          <button
            onClick={() => setShowAllBadges(!showAllBadges)}
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200"
          >
            {showAllBadges ? 'Show Less' : `Show All (${badges.length})`}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-6 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <BadgeStats stats={stats} />


          {/* Badges Grid */}
          {visibleBadges.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {visibleBadges.map((badge, index) => (
                <BadgeCard key={badge.id} badge={badge} index={index} navigate={navigate} />
              ))}
            </div>
          ) : (
            <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-6 text-center">
              <p className="text-gray-400">No badges available.</p>
            </div>
          )}

          {/* Show completion message if all badges earned */}
          {stats.completion === 100 && stats.total > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-lg text-center">
              <h3 className="text-lg font-medium text-green-400 mb-2">🎉 Badge Master!</h3>
              <p className="text-gray-300">Congratulations! You've earned all available badges!</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BadgesSection;